import { useRef } from "react";
import { Calendar, RefreshCw } from "lucide-react";

import { clsxMerge, safeGetDate } from "shared/lib/helpers";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import {
  CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP,
  CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP,
} from "shared/lib/constants/meeting-acceptance";
import { Avatar, AvatarFallback } from "shared/ui/shadcn/avatar";
import { DATE_FORMAT_WITHOUT_TZ } from "shared/lib/constants/time";

import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { useQueryFetchBookedMeetingCalendarEventForAccount } from "@/modules/pipeline/account-details/data/queries";
import { getFallbackTextForBookedMeetingEventAttendeeAvatar } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/utils";
import { useMutationRequestSyncCalendarEventForAccount } from "@/modules/pipeline/account-details/data/mutations";
import { ACCOUNTS_DETAILS_TABS } from "@/modules/pipeline/account-details/constants";
import { BookedMeetingCalendarEventRibbonStates } from "./states";
import { JoinMeetingLink } from "@/modules/pipeline/account-details/shared/booked-meeting-calendar-event/join-meeting-link";
import { useScrollShadows } from "./hooks";

export const BookedMeetingCalendarEventRibbon = ({
  className,
}: PropsWithClassNameI) => {
  const {
    accountStatus,
    account,
    campaign,
    activeTab,
    onAccountUpdate,
    accountHistoryData,
  } = useAccountDetailsContext();

  const bookedMeetingCalendarEventApi =
    useQueryFetchBookedMeetingCalendarEventForAccount(
      campaign?.id,
      account?.id
    );

  const { mutateAsync, isPending } =
    useMutationRequestSyncCalendarEventForAccount();

  const handleSyncCalendarEvent = async () => {
    try {
      const event = await mutateAsync({
        accountId: account?.id,
        campaignId: campaign?.id,
      });

      const isScheduled = Boolean(!bookedMeetingCalendarEventApi.data && event);

      const isRescheduled = Boolean(
        accountStatus?.meeting_time &&
        event &&
        accountStatus.meeting_time !== event.meeting_date
      );

      const isCanceled = Boolean(
        bookedMeetingCalendarEventApi.data && event === null
      );

      // Trigger account update if the meeting was scheduled, rescheduled or canceled
      if (isScheduled || isRescheduled || isCanceled) {
        onAccountUpdate?.();

        if (activeTab === ACCOUNTS_DETAILS_TABS.HISTORY) {
          accountHistoryData?.reloadData();
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const meeting = bookedMeetingCalendarEventApi.data;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { showLeftShadow, showRightShadow } = useScrollShadows(scrollRef);

  return (
    <div
      className={clsxMerge(
        "border-b border-[#D5D5D5] bg-white px-4 py-2",
        className
      )}
    >
      <DataStatesWrapper
        api={bookedMeetingCalendarEventApi}
        loading={<BookedMeetingCalendarEventRibbonStates.Pending />}
        error={
          <BookedMeetingCalendarEventRibbonStates.Error
            onRetry={bookedMeetingCalendarEventApi.refetch}
          />
        }
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <Calendar size={18} className="flex-shrink-0 text-[#4474E3]" />

            <div className="flex min-w-0 flex-1 items-center gap-3">
              <span className="font-semibold typography-body-4">
                Calendar event:
              </span>

              {meeting && meeting.event_exists ? (
                <>
                  {meeting.meeting_date && (
                    <span className="text-black/70 typography-body-4">
                      {safeGetDate(
                        meeting.meeting_date,
                        DATE_FORMAT_WITHOUT_TZ
                      )}
                    </span>
                  )}

                  <JoinMeetingLink
                    className="flex-shrink-0"
                    conferenceUrl={meeting.conference_url}
                  />

                  <div className="relative min-w-0 flex-1">
                    {/* Scrollable attendees container */}
                    <div
                      ref={scrollRef}
                      className="flex items-center gap-2 overflow-x-auto"
                      style={{
                        scrollbarWidth: "none",
                        msOverflowStyle: "none",
                        WebkitOverflowScrolling: "touch",
                      }}
                    >
                      {meeting.attendees.map((attendee, idx) => (
                        <div
                          key={idx}
                          className="flex flex-shrink-0 items-center gap-1.5 rounded bg-gray-50 px-2 py-0.5"
                        >
                          <Avatar className="h-5 w-5">
                            <AvatarFallback className="typography-body-6">
                              {getFallbackTextForBookedMeetingEventAttendeeAvatar(
                                attendee
                              )}
                            </AvatarFallback>
                          </Avatar>

                          <span
                            className="max-w-[180px] truncate typography-body-5"
                            title={attendee.name || attendee.email}
                          >
                            {attendee.name || attendee.email}
                          </span>

                          {attendee.is_organizer && (
                            <span className="text-muted-foreground typography-body-6">
                              (Organizer)
                            </span>
                          )}

                          <span className="text-muted-foreground">
                            &middot;
                          </span>

                          <span
                            className={clsxMerge(
                              "typography-body-6",
                              CALENDAR_ACCEPTANCE_STATUS_COLORS_MAP[
                                attendee.acceptance_status
                              ]
                            )}
                          >
                            {
                              CALENDAR_ACCEPTANCE_STATUS_LABELS_MAP[
                                attendee.acceptance_status
                              ]
                            }
                          </span>
                        </div>
                      ))}

                      <span className="ml-2 flex-shrink-0 text-muted-foreground typography-body-5">
                        Updated:{" "}
                        {safeGetDate(
                          meeting.updated_at,
                          DATE_FORMAT_WITHOUT_TZ
                        )}
                      </span>
                    </div>

                    {/* Left shadow - positioned after scrollable content for proper z-index */}
                    {showLeftShadow && (
                      <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-8 bg-gradient-to-r from-white to-transparent" />
                    )}

                    {/* Right shadow */}
                    {showRightShadow && (
                      <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-8 bg-gradient-to-l from-white to-transparent" />
                    )}
                  </div>
                </>
              ) : (
                <span className="italic text-black/40 typography-body-4">
                  No calendar event found
                </span>
              )}
            </div>
          </div>

          <button
            disabled={isPending}
            onClick={handleSyncCalendarEvent}
            className={clsxMerge(
              "flex items-center gap-1.5 rounded px-3 py-1",
              "text-[#4474E3] hover:bg-[#4474E3]/10",
              "transition-colors disabled:opacity-50",
              "flex-shrink-0 whitespace-nowrap typography-body-5-medium"
            )}
          >
            <RefreshCw
              size={14}
              className={clsxMerge(isPending && "animate-spin")}
            />
            {isPending ? "Syncing..." : "Refresh"}
          </button>
        </div>
      </DataStatesWrapper>
    </div>
  );
};
